import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Search, ArrowRight, Star, Shield, Clock, Users, Building2, CheckCircle2, ChevronDown, ChevronUp, ChevronLeft, Sparkles } from 'lucide-react';
import { getCategories, getCompanies } from '@/lib/api';
import CompanyCard from '@/components/CompanyCard';
import { logger } from '@/lib/logger';

// Structured Categories with main/sub navigation
const CategoriesGrid = ({ structuredCategories, flatCategories, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);

  const sortedGroups = Object.keys(structuredCategories).sort((a, b) => a.localeCompare(b, 'da'));
  // If there is only one group we render the flat list directly – no interstitial "group" card.
  const flatMode = sortedGroups.length <= 1;

  if (flatMode) {
    const cats = flatCategories || structuredCategories[sortedGroups[0]] || [];
    const visible = expanded ? cats : cats.slice(0, 8);
    return (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {visible.map((category, index) => (
            <Card
              key={category}
              className="group cursor-pointer hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              onClick={() => navigate(`/directory?category=${encodeURIComponent(category)}`)}
              data-testid={`category-card-${index}`}
            >
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                  {category}
                </h3>
              </CardContent>
            </Card>
          ))}
        </div>
        {cats.length > 8 && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setExpanded(!expanded)}
              className="gap-2"
              data-testid="toggle-categories-button"
            >
              {expanded ? (<>Vis færre <ChevronUp className="w-4 h-4" /></>) : (<>Vis alle {cats.length} kategorier <ChevronDown className="w-4 h-4" /></>)}
            </Button>
          </div>
        )}
      </>
    );
  }

  if (activeGroup) {
    const subcats = structuredCategories[activeGroup] || [];
    return (
      <>
        <Button 
          variant="ghost" 
          onClick={() => setActiveGroup(null)}
          className="mb-6 gap-2"
          data-testid="back-to-categories"
        >
          <ChevronLeft className="w-4 h-4" />
          Tilbage til kategorier
        </Button>
        <h3 className="text-xl font-semibold mb-4">{activeGroup}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {subcats.map((category, index) => (
            <Card 
              key={category}
              className="group cursor-pointer hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              onClick={() => navigate(`/directory?category=${encodeURIComponent(category)}`)}
              data-testid={`subcategory-card-${index}`}
            >
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                  {category}
                </h3>
              </CardContent>
            </Card>
          ))}
        </div>
      </>
    );
  }

  const visibleGroups = expanded ? sortedGroups : sortedGroups.slice(0, 4);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {visibleGroups.map((group, index) => (
          <Card 
            key={group}
            className="group cursor-pointer hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
            onClick={() => setActiveGroup(group)}
            data-testid={`category-group-${index}`}
          >
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                {group}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {structuredCategories[group].length} typer
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {sortedGroups.length > 4 && (
        <div className="text-center mt-8">
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => setExpanded(!expanded)}
            className="gap-2"
            data-testid="toggle-categories-button"
          >
            {expanded ? (
              <>
                Se mindre
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                Se flere kategorier
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [structuredCategories, setStructuredCategories] = useState({});
  const [featuredCompanies, setFeaturedCompanies] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, compRes] = await Promise.all([
          getCategories(),
          getCompanies({ sort_by: 'rating', sort_order: 'desc' })
        ]);
        setCategories(catRes.data.categories);
        setStructuredCategories(catRes.data.structured || {});
        setFeaturedCompanies(compRes.data.slice(0, 3));
        setTotalCompanies(compRes.data.length);
      } catch (error) {
        logger.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets-gfyr7b9c.emergentagent.net/job_e325e302-32e6-4ae2-9988-c7526b7c166a/artifacts/6rmbez77_image.png)' }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
          <div className="max-w-3xl stagger-children">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Find den perfekte
              <span className="block bronze-text">håndværker</span>
              til dit projekt
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
              Læs anmeldelser og få tilbud fra kvalificerede håndværkere i hele Danmark.
            </p>

            {/* Search Box */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Søg efter håndværker eller service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-base bg-white border-0 shadow-lg"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit" 
                size="lg" 
                className="h-14 px-8 bronze-shine border-0 text-white shadow-lg hover:opacity-90"
                data-testid="hero-search-button"
              >
                Søg nu
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>

            {/* Auto match CTA */}
            <div className="mt-4 max-w-xl">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/find-automatisk')}
                className="text-white hover:text-white hover:bg-white/10 border border-white/30 backdrop-blur-sm"
                data-testid="hero-auto-match-button"
              >
                <Sparkles className="w-4 h-4 mr-2 text-[#c97f59]" />
                Eller lad os finde en håndværker automatisk
              </Button>
            </div>

            {/* Quick Stats — se removed-hero-quick-stats.snippet.jsx for at genindsætte */}
          </div>
        </div>
      </section>

      {/* Trustpilot Section — clickable logo linking to Vibra's Trustpilot review page */}
      <section className="py-8 bg-white border-b" data-testid="trustpilot-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-center">
          <a
            href="https://dk.trustpilot.com/review/vibra.dk"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Se Vibras anmeldelser på Trustpilot"
            className="inline-flex items-center gap-2 group transition-transform hover:scale-105"
            data-testid="trustpilot-link"
          >
            <Star className="w-7 h-7 text-[#00b67a] fill-[#00b67a] group-hover:brightness-110" />
            <span className="font-bold text-xl text-foreground group-hover:text-[#00b67a] transition-colors">
              Trustpilot
            </span>
          </a>
        </div>
      </section>

      {/* Categories Section - Show 8 initially with expand option */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Kategorier
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Find den rette håndværker til dit projekt blandt vores mange kategorier
            </p>
          </div>

          <CategoriesGrid structuredCategories={structuredCategories} flatCategories={categories} navigate={navigate} />
        </div>
      </section>

      {/* Featured Companies */}
      {featuredCompanies.length > 0 && (
        <section className="py-20 md:py-28 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Topbedømte håndværkere
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Se vores mest populære og bedst anmeldte virksomheder
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredCompanies.map((company) => (
                <CompanyCard key={company.id} company={company} />
              ))}
            </div>

            <div className="text-center mt-10">
              <Button 
                size="lg"
                onClick={() => navigate('/directory')}
                className="gap-2 bg-primary hover:bg-primary/90"
                data-testid="view-all-companies-button"
              >
                Se alle håndværkere
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* How it Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 id="nav-hide-trigger" className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Sådan fungerer det
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Find og kontakt håndværkere på få enkle trin
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                title: 'Søg og filtrer',
                description: 'Brug vores avancerede søgning til at finde den perfekte håndværker baseret på rating og kategori.'
              },
              {
                icon: Star,
                title: 'Læs anmeldelser',
                description: 'Læs ægte anmeldelser fra tidligere kunder og sammenlign håndværkernes bedømmelser.'
              },
              {
                icon: Clock,
                title: 'Få tilbud hurtigt',
                description: 'Send en tilbudsanmodning og modtag svar fra interesserede håndværkere inden for kort tid.'
              }
            ].map((step, index) => (
              <div key={step.title} className="text-center">
                <div className="relative inline-flex mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bronze-shine text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Er du håndværker?
              </h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                Bliv en del af Vibra og få adgang til tusindvis af potentielle kunder. 
                Opret din virksomhedsprofil i dag og start med at modtage tilbudsanmodninger.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Kontakt med kvalificerede kunder',
                  'Fremhævet profil med Pro-abonnement',
                  'Verificeret badge for øget troværdighed'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#c97f59]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                size="lg"
                onClick={() => navigate('/register-company')}
                className="bg-white text-primary hover:bg-white/90 gap-2"
                data-testid="cta-register-company"
              >
                Opret virksomhed
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://customer-assets-lxgj4vgw.emergentagent.net/job_8336221e-1edc-4c2e-9baa-9789449b24b3/artifacts/ipt56q14_image.png"
                alt="Smilende håndværker der pudser en væg"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Landing Page Version */}
      <footer className="bg-[#0F1C19] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Vibra" className="h-6 w-auto object-contain brightness-0 invert" />
                <h4 className="text-lg font-semibold text-white">Vibra</h4>
              </div>
              <p className="text-white/60 max-w-md leading-relaxed">
                Find og sammenlign kvalificerede håndværkere. 
                Vi forbinder husejere med de bedste professionelle.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-lg">Links</h4>
              <ul className="space-y-3 text-white/60">
                <li><a href="/directory" className="hover:text-white transition-colors">Find Håndværker</a></li>
                <li><a href="/abonnement" className="hover:text-white transition-colors">Abonnement</a></li>
                <li><a href="/register" className="hover:text-white transition-colors">Opret konto</a></li>
                <li><a href="/register-company" className="hover:text-white transition-colors">Virksomhed</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-lg">Kontakt</h4>
              <ul className="space-y-3 text-white/60">
                <li>kontakt@vibra.dk</li>
                <li>CVR-nr: 46663772</li>
                <li>vibra.dk</li>
                <li className="pt-2 text-white/40 text-xs">
                  Mail svartider: Fre-Søn 14:00-18:00
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40">
            © 2026 Vibra. Alle rettigheder forbeholdes.
          </div>
        </div>
      </footer>
    </div>
  );
}
